import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { XMarkIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import { ReactEditor, useSlateStatic } from "slate-react";
import { Transforms, Element, Path } from "slate";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  AITemplateName,
  AITemplateContent,
  AIContentElement,
} from "../types/ai-templates";
import { useAIContentRefContext } from "@packages/contexts/AIContentRefContext";
import {
  TimelineTemplate,
  TimelineTemplate3,
  TimelineTemplate4,
  TimelineTemplate5,
  ComparisonTemplateMulti,
  CycleTemplate3,
  CycleTemplate4,
  CycleTemplate5,
  HierarchyTemplate,
  FlowchartTemplate,
  FlowchartTemplate2,
  FlowchartTemplate3,
  FlowchartTemplate4,
  FlowchartTemplate5,
  FlowchartTemplate6,
} from "./ai-templates";
import { ExportMenu } from "./export-menu";
import { Autographics } from "./Autographics";

type TemplateComponent =
  | typeof TimelineTemplate
  | typeof TimelineTemplate3
  | typeof TimelineTemplate4
  | typeof TimelineTemplate5
  | typeof ComparisonTemplateMulti
  | typeof CycleTemplate3
  | typeof CycleTemplate4
  | typeof CycleTemplate5
  | typeof HierarchyTemplate
  | typeof FlowchartTemplate
  | typeof FlowchartTemplate2
  | typeof FlowchartTemplate3
  | typeof FlowchartTemplate4
  | typeof FlowchartTemplate5
  | typeof FlowchartTemplate6;

type TemplateRegistry = {
  default: TemplateComponent;
  [key: string]: TemplateComponent;
};

const TEMPLATE_COMPONENTS: {
  [key in AITemplateName]: TemplateRegistry;
} = {
  timeline: {
    default: TimelineTemplate,
    TimelineTemplate_2: TimelineTemplate,
    TimelineTemplate_3: TimelineTemplate3,
    TimelineTemplate_4: TimelineTemplate4,
    TimelineTemplate_5: TimelineTemplate5,
  },
  comparison: {
    default: ComparisonTemplateMulti,
    ComparisonTemplate_2: ComparisonTemplateMulti,
    ComparisonTemplate_3: ComparisonTemplateMulti,
    ComparisonTemplate_4: ComparisonTemplateMulti,
  },
  cycle: {
    default: CycleTemplate3,
    CycleTemplate_3: CycleTemplate3,
    CycleTemplate_4: CycleTemplate4,
    CycleTemplate_5: CycleTemplate5,
  },
  hierarchy: {
    default: HierarchyTemplate,
    HierarchyTemplate_2_3: HierarchyTemplate,
    HierarchyTemplate_2_4: HierarchyTemplate,
    HierarchyTemplate_3_8: HierarchyTemplate,
  },
  flowchart: {
    default: FlowchartTemplate,
    FlowchartTemplate_2: FlowchartTemplate2,
    FlowchartTemplate_3: FlowchartTemplate3,
    FlowchartTemplate_4: FlowchartTemplate4,
    FlowchartTemplate_5: FlowchartTemplate5,
    FlowchartTemplate_6: FlowchartTemplate6,
  },
};

interface AIContentNodeProps {
  children?: ReactNode;
  element: AIContentElement;
}

// ✅ Helper function to determine template type from content structure
const getTemplateTypeFromContent = (
  content: AITemplateContent,
): AITemplateName => {
  if ("sides" in content && Array.isArray(content.sides)) {
    return "comparison";
  }
  if ("levels" in content && Array.isArray(content.levels)) {
    if (content.levels.length > 1) {
      return "hierarchy";
    }
    // Single level - could be converted to steps
    return "timeline";
  }
  if ("steps" in content && Array.isArray(content.steps)) {
    return "timeline"; // Default for steps-based content
  }
  return "timeline"; // Safe default
};

// ✅ Helper function to get component name parts
const parseComponentName = (componentName: string) => {
  // Parse "ComparisonTemplate_2", "TimelineTemplate_3", etc.
  const parts = componentName.split("_");
  const baseName = parts[0]; // "ComparisonTemplate"
  const number = parts[1] ? parseInt(parts[1]) : null; // 2, 3, etc.

  return { baseName, number };
};

export function AIContentNode({ children, element }: AIContentNodeProps) {
  const editor = useSlateStatic();
  const contentRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const { registerRef, unregisterRef } = useAIContentRefContext();
  const [isAutographicsOpen, setIsAutographicsOpen] = useState(false);

  const handleRemove = useCallback(() => {
    try {
      const currentPath = ReactEditor.findPath(editor, element);
      Transforms.removeNodes(editor, {
        at: currentPath,
        match: (n) =>
          Element.isElement(n) && n.type === "ai-content" && n === element,
      });
    } catch (error) {
      console.error("Error removing AI content node:", error);
    }
  }, [editor, element]);

  const handleUpdate = useCallback(
    (newContent: AITemplateContent) => {
      try {
        Transforms.setNodes(
          editor,
          { template_content: newContent } as Partial<AIContentElement>,
          { at: ReactEditor.findPath(editor, element) },
        );
      } catch (error) {
        console.error("Error updating template content:", error);
      }
    },
    [editor, element],
  );

  // Handle template selection from Autographics
  const handleSelectTemplate = useCallback(
    (template: string, content: AITemplateContent) => {
      console.log("🎯 Template changed to:", template, content);
      try {
        setIsAutographicsOpen(false);
      } catch (error) {
        console.error("Error updating template:", error);
      }
    },
    [],
  );

  // Handle Autographics close
  const handleAutographicsClose = useCallback(() => {
    setIsAutographicsOpen(false);
  }, []);

  // Get current path for Autographics
  const getCurrentPath = useCallback(() => {
    try {
      return ReactEditor.findPath(editor, element);
    } catch (error) {
      console.error("Error getting current path:", error);
      return [0]; // fallback path
    }
  }, [editor, element]);

  useEffect(() => {
    const node = contentRef.current;
    if (node) {
      registerRef(node);
      return () => unregisterRef(node);
    }
  }, [registerRef, unregisterRef]);

  const renderTemplate = () => {
    const content = element.template_content;
    if (!content) {
      console.error("Template content is missing for element:", element);
      return (
        <div className="p-4 text-red-500">
          {t("errors.missingTemplateContent")}
        </div>
      );
    }

    const selectedTemplate = element.selected_template || "";
    console.log("🎨 Rendering template:", selectedTemplate, content);

    // ✅ IMPROVED: Better template type detection
    const contentBasedType = getTemplateTypeFromContent(content);
    const { baseName, number } = parseComponentName(selectedTemplate);

    console.log("📊 Content analysis:", {
      selectedTemplate,
      contentBasedType,
      baseName,
      number,
      contentStructure: Object.keys(content),
    });

    // ✅ IMPROVED: Determine base type from component name or content
    let baseType: AITemplateName;

    if (baseName.toLowerCase().includes("comparison")) {
      baseType = "comparison";
    } else if (baseName.toLowerCase().includes("timeline")) {
      baseType = "timeline";
    } else if (baseName.toLowerCase().includes("cycle")) {
      baseType = "cycle";
    } else if (baseName.toLowerCase().includes("hierarchy")) {
      baseType = "hierarchy";
    } else if (baseName.toLowerCase().includes("flowchart")) {
      baseType = "flowchart";
    } else {
      // Fallback to content-based detection
      baseType = contentBasedType;
      console.warn("⚠️ Using content-based type detection:", baseType);
    }

    console.log("🎯 Determined base type:", baseType);

    // ✅ IMPROVED: Validate content structure matches template type
    const isContentCompatible = (
      type: AITemplateName,
      content: AITemplateContent,
    ): boolean => {
      switch (type) {
        case "comparison":
          return "sides" in content && Array.isArray(content.sides);
        case "hierarchy":
          return "levels" in content && Array.isArray(content.levels);
        case "timeline":
        case "cycle":
        case "flowchart":
          return (
            ("steps" in content && Array.isArray(content.steps)) ||
            ("levels" in content && Array.isArray(content.levels))
          );
        default:
          return false;
      }
    };

    if (!isContentCompatible(baseType, content)) {
      console.error(
        `❌ Content incompatible with template type ${baseType}:`,
        content,
      );

      // Try to find a compatible type
      const compatibleType = getTemplateTypeFromContent(content);
      if (compatibleType !== baseType) {
        console.log(`🔄 Switching to compatible type: ${compatibleType}`);
        baseType = compatibleType;
      } else {
        return (
          <div className="p-4 text-red-500">
            Template content is incompatible with selected template type.
            <br />
            Expected: {baseType}, Content: {Object.keys(content).join(", ")}
          </div>
        );
      }
    }

    const templateRegistry = TEMPLATE_COMPONENTS[baseType];
    if (!templateRegistry) {
      console.warn(`❌ Unknown AI template type: ${baseType}`);
      return (
        <div className="p-4 text-red-500">
          {t("errors.unknownTemplateType", { type: baseType })}
        </div>
      );
    }

    // ✅ IMPROVED: Better component selection
    let TemplateComponent = templateRegistry.default;

    // Try exact match first
    if (templateRegistry[selectedTemplate]) {
      TemplateComponent = templateRegistry[selectedTemplate];
      console.log("✅ Found exact template match:", selectedTemplate);
    } else {
      // Try to find by number if available
      if (number && number >= 2 && number <= 6) {
        const numberBasedKey = `${
          baseType === "comparison"
            ? "ComparisonTemplate"
            : baseType === "timeline"
              ? "TimelineTemplate"
              : baseType === "cycle"
                ? "CycleTemplate"
                : baseType === "hierarchy"
                  ? "HierarchyTemplate"
                  : "FlowchartTemplate"
        }_${number}`;

        if (templateRegistry[numberBasedKey]) {
          TemplateComponent = templateRegistry[numberBasedKey];
          console.log("✅ Found number-based template match:", numberBasedKey);
        } else {
          console.warn(
            `⚠️ No template found for ${numberBasedKey}, using default`,
          );
        }
      } else {
        console.warn(
          `⚠️ No exact match for ${selectedTemplate}, using default for ${baseType}`,
        );
      }
    }

    // ✅ FINAL SAFETY CHECK: Ensure component exists
    if (!TemplateComponent) {
      console.error(`❌ No template component found for ${baseType}`);
      return (
        <div className="p-4 text-red-500">
          Template component not found for type: {baseType}
        </div>
      );
    }

    console.log(
      "🚀 Rendering component:",
      TemplateComponent.name,
      "with content:",
      content,
    );

    try {
      return <TemplateComponent content={content} onUpdate={handleUpdate} />;
    } catch (error) {
      console.error("❌ Error rendering template component:", error);
      return (
        <div className="p-4 text-red-500">
          Error rendering template: {error.message}
          <br />
          <small>
            Template: {selectedTemplate}, Type: {baseType}
          </small>
        </div>
      );
    }
  };

  return (
    <div contentEditable={false} className="relative my-4 select-none">
      <span style={{ display: "none" }}>{children}</span>
      <div className="w-full border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm overflow-hidden group relative">
        <div className="absolute top-2 right-2 z-50 flex gap-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 transform translate-y-2 group-hover:translate-y-0">
          <div className="relative max-h">
            <button
              onClick={() => setIsAutographicsOpen((prev) => !prev)}
              className="p-1 rounded-full bg-gray-100/80 dark:bg-gray-700/80 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              aria-label="Change template"
              title={t("articleGenerator.selectTemplate")}
            >
              <ChevronDownIcon className="w-5 h-5 text-gray-600 dark:text-gray-300" />
            </button>
            {isAutographicsOpen && (
              <div className="absolute right-0 rounded-md overflow-hidden animate-fade-in">
                <Autographics
                  editor={editor}
                  autoCalculatePosition={false}
                  aiResult={element.aiResult}
                  targetPath={getCurrentPath()}
                  onClose={handleAutographicsClose}
                  onSelectTemplate={handleSelectTemplate}
                />
              </div>
            )}
          </div>
          <ExportMenu
            contentRef={contentRef}
            fileName={`${element.selected_template}-content`}
          />

          <button
            onClick={handleRemove}
            className="p-1 rounded-full bg-gray-100/80 dark:bg-gray-700/80 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            aria-label="Remove AI content"
          >
            <XMarkIcon className="w-5 h-5 text-gray-600 dark:text-gray-300" />
          </button>
        </div>
        <motion.div
          ref={contentRef}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
        >
          {renderTemplate()}
        </motion.div>
      </div>
    </div>
  );
}
